import React from 'react';
import { Box, IconButton, TextField, Typography } from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { CustomButton } from '../Infra/CustomButton/CustomButton';
import { LoginFormData, LoginFormErrors } from './types';

interface LoginFormProps {
  formData: LoginFormData;
  formErrors: LoginFormErrors;
  showPassword: boolean;
  isLoading: boolean;
  error: string | null;
  onInputChange: (field: keyof LoginFormData, value: string | boolean) => void;
  onSubmit: (e: React.FormEvent) => void;
  onTogglePassword: () => void;
}

const LoginForm: React.FC<LoginFormProps> = ({
  formData,
  formErrors,
  showPassword,
  isLoading,
  error,
  onInputChange,
  onSubmit,
  onTogglePassword,
}) => {
  return (
    <Box className="login-form">
      <Box className="login-form__header">
        <h1>Sign in</h1>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Sign in to manage your wealth portfolio
        </Typography>
      </Box>

      <form onSubmit={onSubmit} data-testid="login-form">
        {(error || formErrors.general) && (
          <Box
            className="login-form__general-error"
            data-testid="login-error-message"
          >
            {error || formErrors.general}
          </Box>
        )}

        <TextField
          variant="outlined"
          InputLabelProps={{ shrink: true }}
          data-testid="login-email-input"
          id="email"
          label="E-Mail address"
          type="email"
          placeholder="yourname@example.com"
          value={formData.email}
          onChange={e => onInputChange('email', e.target.value)}
          error={!!formErrors.email}
          helperText={formErrors.email}
          disabled={isLoading}
          fullWidth
          sx={{ mb: 2 }}
        />

        <TextField
          variant="outlined"
          InputLabelProps={{ shrink: true }}
          data-testid="login-password-input"
          id="password"
          label="Password"
          type={showPassword ? 'text' : 'password'}
          placeholder="••••••••"
          value={formData.password}
          onChange={e => onInputChange('password', e.target.value)}
          error={!!formErrors.password}
          helperText={formErrors.password}
          disabled={isLoading}
          fullWidth
          sx={{ mb: 2 }}
          InputProps={{
            endAdornment: (
              <IconButton
                data-testid="password-visibility-toggle"
                onClick={onTogglePassword}
                size="small"
                tabIndex={-1}
                edge="end"
              >
                {showPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            ),
          }}
        />

        <CustomButton
          data-testid="login-submit-btn"
          type="submit"
          variant="contained"
          loading={isLoading}
          fullWidth
          size="large"
          sx={{ mt: 2, py: 1.5 }}
        >
          Login
        </CustomButton>
      </form>
    </Box>
  );
};

export default LoginForm;
